import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { WorkingDays } from '@mmx/shared'
import { last } from 'lodash'
import moment from 'moment-timezone'

import { TimeSlotPickerDateRange } from '../time-slot-picker/time-slot-picker.component'

@Component({
  selector: 'mmx-time-slot-picker-custom',
  templateUrl: './time-slot-picker-custom.component.html',
  styleUrls: ['./time-slot-picker-custom.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TimeSlotPickerCustomComponent implements OnChanges {
  @Input()
  public timeSlots: moment.Moment[] = []

  @Input()
  public selectedTimeSlots: moment.Moment[] | null = []

  @Input()
  public dateRange: TimeSlotPickerDateRange | null = null

  @Input()
  public timezone = 'America/New_York'

  @Input()
  public workingDays: WorkingDays = {}

  @Input()
  public readonly? = false

  @Output()
  public add = new EventEmitter<moment.Moment>()

  @Output()
  public remove = new EventEmitter<moment.Moment>()

  @Output()
  public dateRangeChange = new EventEmitter<{ from: string, to: string }>()

  public atBeginning = true
  public availableDates: TimeSlotDateData[] = []
  public timeSlotsForSelectedDate: moment.Moment[] = []
  public amTimeSlots: moment.Moment[] = []
  public pmTimeSlots: moment.Moment[] = []
  public visible = false
  public timesSlotPickerVisible = false
  public datePickerVisible = false
  public selectedDate: moment.Moment | undefined

  public get canGetPrevious() {
    const earliestDate = this.timeSlots[0]
    const today = moment().tz(this.timezone).startOf('day')

    if (!earliestDate) {
      return false
    }

    return !earliestDate.isSame(today, 'day') || earliestDate.isBefore(today, 'day')
  }

  public ngOnChanges({ timeSlots, readonly }: SimpleChanges): void {
    if (!timeSlots?.currentValue) {
      return
    }

    if ((!timeSlots.currentValue.length || readonly?.currentValue === true) && !this.visible) {
      this.onToggle()
    }

    const availableDates = []
    const from = moment.tz(this.timeSlots[0] ?? this.dateRange?.from, this.timezone)

    for (let i = 0; i < 7; i++) {
      const date = from.clone().add(i, 'day')
      availableDates.push({
        month: date.format('MMM'),
        day: date.format('Do'),
        weekday: date.format('ddd'),
        date: date.format('YYYY-MM-DD'),
        availableSlotsCount: this.timeSlots.filter(timeSlot => timeSlot.isSame(date, 'day')).length,
        isFacilityClosed: !!this.workingDays[date.format('ddd').toLowerCase() as keyof WorkingDays]?.isClosed,
      })
    }

    this.availableDates = availableDates
  }

  public onTimePicked(timeSlot: moment.Moment) {
    if (this.isTimeSlotSelected(timeSlot)) {
      this.remove.emit(timeSlot)
    } else {
      this.add.emit(timeSlot)
    }
  }

  public isDaySelected(date: TimeSlotDateData) {
    return this.selectedTimeSlots?.some(selectedTimeSlot => selectedTimeSlot.isSame(date.date, 'day'))
  }

  public isTimeSlotSelected(timeSlot: moment.Moment) {
    return this.selectedTimeSlots?.some(selectedTimeSlot => selectedTimeSlot.isSame(timeSlot))
  }

  public reset() {
    this.visible = false
    this.timesSlotPickerVisible = false
    this.datePickerVisible = false
  }

  public onToggle() {
    this.visible = !this.visible
    this.datePickerVisible = this.visible
    this.timesSlotPickerVisible = false
  }

  public onDatePicked({ date, availableSlotsCount }: TimeSlotDateData) {
    if (!availableSlotsCount) {
      return
    }

    this.selectedDate = moment(date)
    this.timeSlotsForSelectedDate = this.timeSlots.filter(timeSlot => timeSlot.format('YYYY-MM-DD') === date)
    this.amTimeSlots = this.timeSlotsForSelectedDate.filter(timeSlot => timeSlot.format('a') === 'am')
    this.pmTimeSlots = this.timeSlotsForSelectedDate.filter(timeSlot => timeSlot.format('a') !== 'am')

    this.datePickerVisible = false
    this.timesSlotPickerVisible = true
  }

  public onBack() {
    this.datePickerVisible = true
    this.timesSlotPickerVisible = false
    this.timeSlotsForSelectedDate = []
    this.selectedDate = undefined
  }

  public onPrevious() {
    const earliestSlot = this.timeSlots[0]
    const rangeFrom = this.dateRange?.from ? moment(this.dateRange.from) : moment()
    const earliestDate = rangeFrom.isBefore(earliestSlot) ? rangeFrom : earliestSlot
    let from = earliestDate.clone().subtract(1, 'week')

    if (from.isBefore(moment().tz(this.timezone).startOf('day'))) {
      this.atBeginning = true
      from = moment().tz(this.timezone)
    }

    this.dateRangeChange.emit({
      from: from.format('YYYY-MM-DD'),
      to: from.clone().add(6, 'days').format('YYYY-MM-DD'),
    })
  }

  public onNext() {
    const latestDate = last(this.timeSlots)!
    let from = latestDate.clone().add(1, 'day')

    this.dateRangeChange.emit({
      from: from.format('YYYY-MM-DD'),
      to: from.clone().add(6, 'days').format('YYYY-MM-DD'),
    })

    this.atBeginning = false
  }
}

interface TimeSlotDateData {
  month: string
  day: string
  weekday: string
  date: string
  availableSlotsCount: number
  isFacilityClosed: boolean
}
